import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { api } from "../api";
import { PublisherMark } from "./Badges";
import { timeAgo } from "../format";
import { useSession } from "../session";
import { Avatar } from "../shell/Avatar";
import { Alert } from "../shell/ui";
import { Icon } from "./Icon";
import { PagedList } from "./PagedList";

export type TargetType = "strategy" | "run" | "bot" | "user";

/** "community resident · simulated" — honest about who is a simulated member (plan H8). */
export function SimBadge() {
  return (
    <span
      className="badge sim-badge"
      title="A simulated community member: its activity is generated, its numbers are real backtests and paper bots."
    >
      simulated
    </span>
  );
}

export function AuthorChip({
  username,
  name,
  avatar,
  simulated,
  badge,
  size = 20,
  suffix,
}: {
  username: string;
  name: string;
  avatar: string;
  simulated?: boolean;
  /** "verified" for a Verified publisher. */
  badge?: "verified" | null;
  size?: number;
  suffix?: ReactNode;
}) {
  return (
    <span className="author">
      <Avatar seed={avatar} size={size} />
      <Link to={`/u/${username}`} onClick={(e) => e.stopPropagation()}>
        {name}
      </Link>
      <PublisherMark badge={badge} />
      {simulated && <SimBadge />}
      {suffix}
    </span>
  );
}

/** Where a guest goes to act: sign in, then come straight back here. */
export function SignInLink({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <Link className="btn btn--sm" to={`/signin?next=${encodeURIComponent(pathname)}`}>
      {children}
    </Link>
  );
}

export function Stars({ value, count, size = "md" }: { value: number; count?: number; size?: "sm" | "md" }) {
  const rounded = Math.round(value * 2) / 2;
  return (
    <span
      className={`stars stars--${size}`}
      aria-label={
        count === 0
          ? "not rated yet"
          : `${value.toFixed(1)} out of 5${count != null ? `, ${count} ratings` : ""}`
      }
    >
      <span aria-hidden="true">
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            className={i <= rounded ? "star is-on" : i - 0.5 === rounded ? "star is-half" : "star"}
          >
            ★
          </span>
        ))}
      </span>
      {count != null && (
        <span className="stars__count">{count === 0 ? "no ratings" : `${value.toFixed(1)} · ${count}`}</span>
      )}
    </span>
  );
}

interface TargetState {
  following: boolean;
  saved: boolean;
  rating: { stars: number; review: string } | null;
  voted: boolean;
}

export function useTargetState(type: TargetType, id: string) {
  const { user } = useSession();
  return useQuery({
    queryKey: ["state", type, id, user?.id ?? "guest"],
    queryFn: () => api<TargetState>(`/hub/state?targetType=${type}&targetId=${encodeURIComponent(id)}`),
    enabled: !!id,
  });
}

function useToggle(kind: "follows" | "saves", type: TargetType, id: string, invalidate: unknown[][]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) =>
      on
        ? api(`/hub/${kind}`, { method: "PUT", body: { targetType: type, targetId: id } })
        : api(`/hub/${kind}?targetType=${type}&targetId=${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["state", type, id] });
      for (const key of invalidate) void qc.invalidateQueries({ queryKey: key });
      // My bots and Backtests list what you follow and save under your own.
      if (kind === "follows" && type === "bot") void qc.invalidateQueries({ queryKey: ["followed-bots"] });
      if (kind === "saves") void qc.invalidateQueries({ queryKey: ["saved-runs"] });
    },
  });
}

export function FollowButton({
  type,
  id,
  invalidate = [],
}: {
  type: TargetType;
  id: string;
  invalidate?: unknown[][];
}) {
  const { user } = useSession();
  const state = useTargetState(type, id);
  const toggle = useToggle("follows", type, id, invalidate);
  if (!user)
    return (
      <SignInLink>
        <Icon name="follow" /> Follow
      </SignInLink>
    );
  const on = state.data?.following ?? false;
  return (
    <button
      type="button"
      className={`btn btn--sm${on ? " is-on" : ""}`}
      aria-pressed={on}
      disabled={toggle.isPending}
      onClick={() => toggle.mutate(!on)}
    >
      <Icon name={on ? "check" : "follow"} />
      {on ? "Following" : "Follow"}
    </button>
  );
}

/**
 * A bookmark for a backtest run. Runs are finished results, so there is
 * nothing to follow; people, strategies and bots are followed instead.
 */
export function SaveButton({ id, invalidate = [] }: { id: string; invalidate?: unknown[][] }) {
  const { user } = useSession();
  const state = useTargetState("run", id);
  const toggle = useToggle("saves", "run", id, invalidate);
  if (!user)
    return (
      <SignInLink>
        <Icon name="save" /> Save
      </SignInLink>
    );
  const on = state.data?.saved ?? false;
  return (
    <button
      type="button"
      className={`btn btn--sm${on ? " is-on" : ""}`}
      aria-pressed={on}
      disabled={toggle.isPending}
      onClick={() => toggle.mutate(!on)}
      title={on ? "Remove from your saved backtests" : "Keep this backtest on your Dashboard"}
    >
      <Icon name={on ? "check" : "save"} />
      {on ? "Saved" : "Save"}
    </button>
  );
}

interface Review {
  stars: number;
  review: string;
  updated_at: string;
  username: string;
  display_name: string;
  avatar_seed: string;
  is_simulated: boolean;
}

/** Your rating (1–5 and an optional short review) and everyone else's. */
export function RatingPanel({
  type,
  id,
  isOwner,
  invalidate = [],
}: {
  type: "strategy" | "run" | "bot";
  id: string;
  isOwner: boolean;
  invalidate?: unknown[][];
}) {
  const { user } = useSession();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["ratings", type, id],
    queryFn: () =>
      api<{ items: Review[]; mine: { stars: number; review: string } | null }>(
        `/hub/ratings?targetType=${type}&targetId=${encodeURIComponent(id)}`,
      ),
  });
  const [stars, setStars] = useState(0);
  const [review, setReview] = useState("");
  const [editing, setEditing] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api("/hub/ratings", { method: "PUT", body: { targetType: type, targetId: id, stars, review } }),
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["ratings", type, id] });
      for (const key of invalidate) void qc.invalidateQueries({ queryKey: key });
    },
  });
  const mine = list.data?.mine;
  const showForm = user && !isOwner && (editing || !mine);

  return (
    <div className="ratings">
      {!user && (
        <p className="muted">
          Sign in to rate this. <SignInLink>Sign in</SignInLink>
        </p>
      )}
      {user && isOwner && (
        <p className="muted small">You can't rate your own work — but you can read what others think.</p>
      )}
      {mine && !editing && (
        <p>
          Your rating: <Stars value={mine.stars} />{" "}
          {mine.review && <em className="muted">“{mine.review}”</em>}{" "}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setStars(mine.stars);
              setReview(mine.review);
              setEditing(true);
            }}
          >
            change
          </button>
        </p>
      )}
      {showForm && (
        <form
          className="rating-form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (stars > 0) save.mutate();
          }}
        >
          <div className="star-input" role="radiogroup" aria-label="Your rating">
            {[1, 2, 3, 4, 5].map((i) => (
              <button
                key={i}
                type="button"
                role="radio"
                aria-checked={stars === i}
                aria-label={`${i} star${i > 1 ? "s" : ""}`}
                className={i <= stars ? "star is-on" : "star"}
                onClick={() => setStars(i)}
              >
                ★
              </button>
            ))}
          </div>
          <input
            placeholder="A short review (optional)"
            value={review}
            maxLength={1000}
            onChange={(e) => setReview(e.target.value)}
          />
          <button type="submit" className="btn btn--primary btn--sm" disabled={stars === 0 || save.isPending}>
            Rate
          </button>
          {save.error && <Alert tone="error">{save.error.message}</Alert>}
        </form>
      )}
      <PagedList
        className="reviews"
        items={(list.data?.items ?? []).filter((r) => r.review)}
        pageSize={10}
        render={(r) => (
          <li key={r.username}>
            <AuthorChip
              username={r.username}
              name={r.display_name}
              avatar={r.avatar_seed}
              simulated={r.is_simulated}
            />
            <Stars value={r.stars} size="sm" />
            <span className="muted small">{timeAgo(r.updated_at)}</span>
            <p>{r.review}</p>
          </li>
        )}
      />
    </div>
  );
}

interface CommentRow {
  id: number;
  parent_id: number | null;
  body: string;
  created_at: string;
  edited_at: string | null;
  deleted: boolean;
  author_id: string;
  username: string;
  display_name: string;
  avatar_seed: string;
  is_simulated: boolean;
  role: string;
}

function CommentForm({
  onSubmit,
  pending,
  placeholder,
  autoFocus,
}: {
  onSubmit: (body: string) => Promise<unknown>;
  pending: boolean;
  placeholder: string;
  autoFocus?: boolean;
}) {
  const [body, setBody] = useState("");
  return (
    <form
      className="comment-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (body.trim()) void onSubmit(body.trim()).then(() => setBody(""));
      }}
    >
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        rows={3}
        maxLength={5000}
        autoFocus={autoFocus}
      />
      <button type="submit" className="btn btn--primary btn--sm" disabled={pending || !body.trim()}>
        Post
      </button>
    </form>
  );
}

/** Comments with one level of replies, as the forum and every public page use them. */
export function CommentsPanel({
  type,
  id,
  invalidate = [],
  title = "Comments",
}: {
  type: "strategy" | "run" | "bot";
  id: string;
  invalidate?: unknown[][];
  title?: string;
}) {
  const { user } = useSession();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["comments", type, id],
    queryFn: () =>
      api<{ items: CommentRow[] }>(`/hub/comments?targetType=${type}&targetId=${encodeURIComponent(id)}`),
  });
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["comments", type, id] });
    for (const key of invalidate) void qc.invalidateQueries({ queryKey: key });
  };
  const post = useMutation({
    mutationFn: (v: { body: string; parentId?: number }) =>
      api("/hub/comments", { method: "POST", body: { targetType: type, targetId: id, ...v } }),
    onSuccess: () => {
      setReplyTo(null);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (cid: number) => api(`/hub/comments/${cid}`, { method: "DELETE" }),
    onSuccess: refresh,
  });

  const items = list.data?.items ?? [];
  const roots = items.filter((c) => c.parent_id === null);
  const replies = (pid: number) => items.filter((c) => c.parent_id === pid);

  const render = (c: CommentRow, nested = false) => (
    <li key={c.id} className={`comment${nested ? " comment--reply" : ""}`} id={`c${c.id}`}>
      <div className="comment__head">
        <AuthorChip
          username={c.username}
          name={c.display_name}
          avatar={c.avatar_seed}
          simulated={c.is_simulated}
          suffix={c.role === "admin" ? <span className="badge badge--accent">team</span> : undefined}
        />
        <span className="muted small" title={new Date(c.created_at).toLocaleString()}>
          {timeAgo(c.created_at)}
          {c.edited_at && " · edited"}
        </span>
      </div>
      {c.deleted ? (
        <p className="muted">
          <em>deleted</em>
        </p>
      ) : (
        <p className="comment__body">{c.body}</p>
      )}
      {user && !c.deleted && (
        <div className="comment__actions">
          {!nested && (
            <button
              type="button"
              className="link-button"
              onClick={() => setReplyTo(replyTo === c.id ? null : c.id)}
            >
              Reply
            </button>
          )}
          {(c.author_id === user.id || user.role === "admin") && (
            <button
              type="button"
              className="link-button"
              onClick={() => window.confirm("Delete this comment?") && remove.mutate(c.id)}
            >
              Delete
            </button>
          )}
        </div>
      )}
      {replies(c.id).length > 0 && (
        <ul className="comments comments--nested">{replies(c.id).map((r) => render(r, true))}</ul>
      )}
      {replyTo === c.id && (
        <CommentForm
          autoFocus
          pending={post.isPending}
          placeholder={`Reply to ${c.display_name}…`}
          onSubmit={(body) => post.mutateAsync({ body, parentId: c.id })}
        />
      )}
    </li>
  );

  return (
    <section className="comments-panel" aria-label={title}>
      <h2 className="section-title">
        {title} <span className="muted">{items.filter((c) => !c.deleted).length}</span>
      </h2>
      {roots.length === 0 && <p className="muted">Nothing yet. {user ? "Start the conversation." : ""}</p>}
      <ul className="comments">{roots.map((c) => render(c))}</ul>
      {post.error && <Alert tone="error">{post.error.message}</Alert>}
      {user ? (
        <CommentForm
          pending={post.isPending}
          placeholder="Add a comment…"
          onSubmit={(body) => post.mutateAsync({ body })}
        />
      ) : (
        <p className="muted">
          <SignInLink>Sign in to comment</SignInLink>
        </p>
      )}
    </section>
  );
}

/** The line every performance number on the hub sits above. */
export function Disclaimer() {
  return (
    <p className="disclaimer muted small">
      Simulated, paper-only results. Past performance — backtested or live on paper — does not predict future
      returns. Not investment advice.
    </p>
  );
}
